import { useEffect, useRef, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { Analytics } from '@vercel/analytics/react';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import './App.css';
import { getPromoActiva } from './api/promociones';
import { CartProvider, useCart } from './context/CartContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { FavoritosProvider } from './context/FavoritosContext';
import Landing from './pages/Landing';
import Home from './pages/Home';
import Success from './pages/Success';
import Failure from './pages/Failure';
import Pending from './pages/Pending';
import Terms from './pages/Terms';
import Faq from './pages/Faq';
import Newsletter from './pages/Newsletter';
import Customize from './pages/Customize';
import Carrito from './pages/Carrito';
import Login from './pages/Login';
import MisPedidos from './pages/MisPedidos';
import Favoritos from './pages/Favoritos';
import MisDirecciones from './pages/MisDirecciones';
import Footer from './components/Footer';

function AnalyticsTracker() {
  const location = useLocation();

  useEffect(() => {
    if (typeof window.gtag !== 'function') {
      return;
    }

    window.gtag('event', 'page_view', {
      page_path: location.pathname + location.search,
      page_title: document.title,
      page_location: window.location.href,
    });
  }, [location]);

  return null;
}

function AccountMenu() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div className="account-menu" ref={menuRef}>
      <button type="button" className="account-menu__trigger" onClick={() => setOpen((prev) => !prev)}>
        Hola, {user.nombre} <span className="account-menu__caret">▾</span>
      </button>
      {open && (
        <div className="account-menu__dropdown">
          <Link className="account-menu__item" to="/mis-direcciones" onClick={() => setOpen(false)}>
            Mis Direcciones
          </Link>
          <Link className="account-menu__item" to="/favoritos" onClick={() => setOpen(false)}>
            Favoritos
          </Link>
          <Link className="account-menu__item" to="/mis-pedidos" onClick={() => setOpen(false)}>
            Mis Pedidos
          </Link>
          <button
            type="button"
            className="account-menu__item account-menu__item--logout"
            onClick={() => {
              setOpen(false);
              logout();
              navigate('/coleccion');
            }}
          >
            Salir
          </button>
        </div>
      )}
    </div>
  );
}

function MainMenu() {
  const location = useLocation();
  const { itemCount } = useCart();
  const { isLoggedIn } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [promoTexto, setPromoTexto] = useState(null);
  const mobileMenuRef = useRef(null);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    let cancelado = false;
    getPromoActiva()
      .then((data) => {
        if (!cancelado) setPromoTexto(data?.texto || null);
      })
      .catch(() => {});
    return () => {
      cancelado = true;
    };
  }, []);

  useEffect(() => {
    if (!mobileMenuOpen) return undefined;

    const handleClickOutside = (event) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [mobileMenuOpen]);

  // The landing page has its own hero and no top nav.
  if (location.pathname === '/') {
    return null;
  }

  return (
    <header className="main-menu" ref={mobileMenuRef}>
      {promoTexto && (
        <div className="promo-banner">
          <span className="promo-banner__text">{promoTexto}</span>
        </div>
      )}
      <nav className="main-menu__nav">
        <button
          type="button"
          className="main-menu__burger"
          aria-label={mobileMenuOpen ? 'Cerrar menú' : 'Abrir menú'}
          aria-expanded={mobileMenuOpen}
          onClick={() => setMobileMenuOpen((prev) => !prev)}
        >
          {mobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
        </button>
        <div className="main-menu__links">
          <Link className="main-menu__link" to="/">
            Inicio
          </Link>
          <Link className="main-menu__link" to="/faq">
            FAQ
          </Link>
          <Link className="main-menu__link main-menu__link--newsletter" to="/newsletter">
            Newsletter
          </Link>
        </div>
        {isLoggedIn ? (
          <AccountMenu />
        ) : (
          <Link className="main-menu__link main-menu__link--login" to="/login">
            Iniciar sesión
          </Link>
        )}
        <Link className="main-menu__link main-menu__link--cart" to="/carrito" aria-label="Carrito">
          <ShoppingCartOutlinedIcon fontSize="small" />
          <span className={`main-menu__cart-badge${itemCount === 0 ? ' main-menu__cart-badge--empty' : ''}`}>
            {itemCount}
          </span>
        </Link>
      </nav>
      {mobileMenuOpen && (
        <div className="main-menu__mobile-panel">
          <Link className="main-menu__mobile-link" to="/" onClick={() => setMobileMenuOpen(false)}>
            Inicio
          </Link>
          <Link className="main-menu__mobile-link" to="/faq" onClick={() => setMobileMenuOpen(false)}>
            FAQ
          </Link>
          <Link className="main-menu__mobile-link" to="/newsletter" onClick={() => setMobileMenuOpen(false)}>
            Newsletter
          </Link>
        </div>
      )}
    </header>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <FavoritosProvider>
          <CartProvider>
            <AnalyticsTracker />
            <MainMenu />
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/coleccion" element={<Home />} />
              <Route path="/coleccion/:categoria" element={<Home />} />
              <Route path="/success" element={<Success />} />
              <Route path="/failure" element={<Failure />} />
              <Route path="/pending" element={<Pending />} />
              {/* Capitalized aliases to match Mercado Pago back_urls */}
              <Route path="/Success" element={<Success />} />
              <Route path="/Failure" element={<Failure />} />
              <Route path="/Pending" element={<Pending />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/faq" element={<Faq />} />
              <Route path="/newsletter" element={<Newsletter />} />
              <Route path="/personalizar/:slug" element={<Customize />} />
              <Route path="/carrito" element={<Carrito />} />
              <Route path="/login" element={<Login />} />
              <Route path="/mis-pedidos" element={<MisPedidos />} />
              <Route path="/favoritos" element={<Favoritos />} />
              <Route path="/mis-direcciones" element={<MisDirecciones />} />
            </Routes>
            <Footer />
          </CartProvider>
        </FavoritosProvider>
      </AuthProvider>
      <Analytics />
    </Router>
  );
}

export default App;
